import type { DeviceTypeFieldDefinition } from "../../api/types";
import { sanitisePhoneNumber } from "../../lib/phone";

// Pure helpers backing the schema-driven config form. Kept separate from the
// component module so the form can never drift from the device-type schema
// (and therefore the managed shadow config) the backend serves, and so React
// Fast Refresh stays happy (the .tsx exports only components).
//
// Form state is a flat Record<string, string> keyed by field path. Values are
// coerced back to their real JSON types (number / boolean / string / array)
// only when building the override payload, because the backend (and the device
// firmware) reject a stringified number or boolean.

export type ConfigValues = Record<string, string>;

export function configFieldId(prefix: string, path: string): string {
  const slug = path
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return `${prefix}-${slug}`;
}

function readValueAtPath(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source) return undefined;
  let current: unknown = source;
  for (const part of path.split(".")) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function setNestedValue(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let current = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    const existing = current[part];
    if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

function deleteNestedValue(target: Record<string, unknown>, path: string): void {
  const parts = path.split(".");
  const parents: Array<{ node: Record<string, unknown>; key: string }> = [];
  let current: unknown = target;
  for (const part of parts.slice(0, -1)) {
    if (!current || typeof current !== "object" || Array.isArray(current)) return;
    parents.push({ node: current as Record<string, unknown>, key: part });
    current = (current as Record<string, unknown>)[part];
  }
  if (!current || typeof current !== "object" || Array.isArray(current)) return;
  delete (current as Record<string, unknown>)[parts[parts.length - 1]];

  for (let index = parents.length - 1; index >= 0; index -= 1) {
    const { node, key } = parents[index];
    const child = node[key];
    if (child && typeof child === "object" && !Array.isArray(child) && Object.keys(child).length === 0) {
      delete node[key];
    }
  }
}

// stringifyFieldValue turns a stored config value (number/bool/string/array)
// into the string the form state holds for that field kind.
export function stringifyFieldValue(field: DeviceTypeFieldDefinition, value: unknown): string {
  if (value === undefined || value === null) return "";
  if (field.kind === "string_array" || field.kind === "phone_array") {
    return Array.isArray(value) ? value.map((item) => String(item)).join("\n") : "";
  }
  if (field.kind === "boolean") {
    return value === true || value === "true" ? "true" : "false";
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return typeof value === "string" ? value : String(value);
}

// initialConfigValues seeds form state for each field from the first source
// that has a value (e.g. [overrides, payload, defaultPayload]). Booleans
// default to "false" so the checkbox renders unchecked rather than empty.
export function initialConfigValues(
  fields: DeviceTypeFieldDefinition[],
  sources: Array<Record<string, unknown> | undefined>,
): ConfigValues {
  const out: ConfigValues = {};
  for (const field of fields) {
    // An array of objects has no flat string form; it is edited through
    // objectArrayDraft / ObjectArrayField instead.
    if (field.kind === "object_array") continue;
    let raw = "";
    for (const source of sources) {
      const value = readValueAtPath(source, field.path);
      if (value === undefined || value === null) continue;
      if (typeof value === "string" && value === "") continue;
      raw = stringifyFieldValue(field, value);
      break;
    }
    if (raw === "" && field.kind === "boolean") raw = "false";
    out[field.path] = raw;
  }
  return out;
}

// ---- secret fields ---------------------------------------------------------
//
// Mirrors devicetype.ValidateSecretValue in the backend: 8–64 printable ASCII
// characters (space to tilde), no leading or trailing space. Empty is not a
// value to send: a save that leaves a secret out keeps the stored one, so a
// blank secret field is simply not sent.

export const SECRET_MIN_LENGTH = 8;
export const SECRET_MAX_LENGTH = 64;

// What a secret value looks like wherever values are listed (diffs, "On
// device"): never the value itself.
export const SECRET_MASK = "••••••••";

// secretValueError explains why a typed secret would be refused, or null when
// it is acceptable. An empty value is not an error here: it means "unchanged".
export function secretValueError(raw: string): string | null {
  if (raw === "") return null;
  if (raw.length < SECRET_MIN_LENGTH || raw.length > SECRET_MAX_LENGTH) {
    return `Must be ${SECRET_MIN_LENGTH}–${SECRET_MAX_LENGTH} characters.`;
  }
  if (!/^[\x20-\x7E]+$/.test(raw)) {
    return "Use only letters, digits, punctuation and spaces (no accents or emoji).";
  }
  if (raw.startsWith(" ") || raw.endsWith(" ")) {
    return "Must not start or end with a space.";
  }
  return null;
}

// invalidSecretFields lists the secret fields whose staged value would be
// refused. Only the given paths are checked (pass the ones a save will send).
export function invalidSecretFields(
  fields: DeviceTypeFieldDefinition[],
  values: ConfigValues,
  paths?: ReadonlySet<string>,
): DeviceTypeFieldDefinition[] {
  return fields.filter(
    (field) =>
      field.kind === "secret" &&
      (!paths || paths.has(field.path)) &&
      secretValueError(values[field.path] ?? "") !== null,
  );
}

export function hasConfigValue(field: DeviceTypeFieldDefinition, raw: string): boolean {
  if (field.kind === "boolean") return true; // a checkbox always has a state
  // A secret is sent exactly as typed; spaces inside it are part of it.
  if (field.kind === "secret") return raw !== "";
  // Phone kinds measure emptiness after sanitising so a value that is nothing
  // but invisible formatting counts as blank, and the key is removed rather
  // than written to the shadow as an empty string or array.
  if (field.kind === "phone_array") {
    return raw.split("\n").map((item) => sanitisePhoneNumber(item)).filter(Boolean).length > 0;
  }
  if (field.kind === "string_array") {
    return raw.split("\n").map((item) => item.trim()).filter(Boolean).length > 0;
  }
  if (field.kind === "number") {
    return raw.trim() !== "" && !Number.isNaN(Number(raw));
  }
  if (field.kind === "phone") return sanitisePhoneNumber(raw).length > 0;
  return raw.trim().length > 0;
}

function coerceConfigValue(field: DeviceTypeFieldDefinition, raw: string): unknown {
  // Phone fields are normalised on the way out so a number pasted from a
  // contacts app (bidi controls, spaces, dashes) reaches the shadow as the
  // digits the user can see. See lib/phone.ts.
  if (field.kind === "phone_array") {
    return raw.split("\n").map((item) => sanitisePhoneNumber(item)).filter(Boolean);
  }
  if (field.kind === "string_array") {
    return raw.split("\n").map((item) => item.trim()).filter(Boolean);
  }
  if (field.kind === "number") return Number(raw);
  if (field.kind === "boolean") return raw === "true";
  if (field.kind === "phone") return sanitisePhoneNumber(raw);
  // Never trimmed: the backend refuses a leading or trailing space instead of
  // silently saving a different password from the one typed.
  if (field.kind === "secret") return raw;
  return raw.trim();
}

// The SOS group and the button settings are saved under a stricter rule than
// the rest of a device's configuration: a key is sent only as a real value, and
// only when it is already stored for the device or an operator changed it. A
// group configuration is deployed straight to the shadow and leaves no
// per-device override, so a default written here on an unrelated save would
// undo it. Both the page that creates a device and its settings page follow
// the rule, so the paths are named once.

// Everything under "buttons" except the contact numbers, which keep the rule
// they always had: a blanked number is removed.
export function isButtonSettingPath(path: string): boolean {
  return path.startsWith("buttons.") && !path.endsWith(".phoneNumber");
}

// The silent listen-in number follows the same rule. It is answered with the
// speaker off, so a number cleared on the page must reach the device as [],
// and an unrelated save must not overwrite one deployed to the device's group.
export const listenInNumbersPath = "inbound.listen_in_numbers";

export function isValueOnlyPath(path: string): boolean {
  return path.startsWith("sos.") || isButtonSettingPath(path) || path === listenInNumbersPath;
}

// buildConfigOverrides converts form state into a typed override payload.
// `base` lets a caller preserve override keys it manages separately (e.g. the
// shadow editor's contact numbers); managed fields are set when they have a
// value and removed when blanked.
export function buildConfigOverrides(
  fields: DeviceTypeFieldDefinition[],
  values: ConfigValues,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  const overrides = JSON.parse(JSON.stringify(base)) as Record<string, unknown>;
  for (const field of fields) {
    // Not representable in flat form state: whatever `base` carries is kept.
    if (field.kind === "object_array") continue;
    const raw = values[field.path] ?? "";
    if (hasConfigValue(field, raw)) {
      setNestedValue(overrides, field.path, coerceConfigValue(field, raw));
    } else {
      deleteNestedValue(overrides, field.path);
    }
  }
  return overrides;
}

// collectLeafPaths records the dotted path of every leaf in a payload (scalar,
// array, or empty object). Arrays are treated as leaves so a phone-array field
// like `inbound.auto_answer_allowlist` is matched whole rather than per-index.
function collectLeafPaths(value: unknown, prefix: string, out: string[]): void {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) {
      if (prefix) out.push(prefix);
      return;
    }
    for (const key of keys) {
      collectLeafPaths((value as Record<string, unknown>)[key], prefix ? `${prefix}.${key}` : key, out);
    }
    return;
  }
  if (prefix) out.push(prefix);
}

// pruneToAllowedPaths drops any leaf whose dotted path is not in `allowedPaths`,
// removing now-empty parent objects too. The device-type schema is the source of
// truth for what the backend accepts, so a stored override that still carries a
// field renamed or removed from the schema (e.g. an old `audio.system_volume`
// after it became `audio.modem_out_gain` / `audio.speaker_amp_level`) would
// otherwise ride along on every save and be rejected as an unsupported field.
// Pruning keeps the submitted payload in lock-step with the current schema.
export function pruneToAllowedPaths(
  payload: Record<string, unknown>,
  allowedPaths: Iterable<string>,
): Record<string, unknown> {
  const allowed = new Set(allowedPaths);
  const result = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
  const leaves: string[] = [];
  collectLeafPaths(result, "", leaves);
  for (const path of leaves) {
    if (!allowed.has(path)) {
      deleteNestedValue(result, path);
    }
  }
  return result;
}

// clearPaths removes each given dotted path from a payload, pruning parents left
// empty. This is what "Reset to defaults" saves: a field reset to its default
// must be ABSENT from the stored override, not written carrying today's default
// value.
//
// Writing it would pin the device to that number. The resolved config would look
// identical today and diverge silently later: a device with an explicit override
// never picks up a change to the schema default, which is how a fleet ends up
// running a superseded value long after the default moved (audio.speaker_amp_level
// 56 -> 63 is the live example). Absent means the device follows the default.
export function clearPaths(
  payload: Record<string, unknown>,
  paths: Iterable<string>,
): Record<string, unknown> {
  const result = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
  for (const path of paths) {
    deleteNestedValue(result, path);
  }
  return result;
}

export function missingRequiredFields(
  fields: DeviceTypeFieldDefinition[],
  values: ConfigValues,
): DeviceTypeFieldDefinition[] {
  return fields.filter(
    (field) => field.required && field.kind !== "object_array" && !hasConfigValue(field, values[field.path] ?? ""),
  );
}

// ---- number formats --------------------------------------------------------
//
// Mirrors devicetype.FieldFormatWeekdayMask / FieldFormatMinuteOfDay in the
// backend. Form state keeps the integer as a string, like any number field;
// these convert it to and from what the controls show.

export const WEEKDAY_MASK_MAX = 127;
export const MINUTE_OF_DAY_MAX = 1439;

// Bit i of a weekday mask is WEEKDAY_NAMES[i]: bit 0 (1) Sunday ... bit 6 (64)
// Saturday.
export const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// parseWholeNumber reads a form value as an integer in 0..max, or null.
function parseWholeNumber(raw: string, max: number): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n <= max ? n : null;
}

// invalidFormattedNumber says whether a weekday_mask or minute_of_day value in
// form state is set but not a whole number in the format's range (e.g. "200"
// for a mask, "1500" or "abc" for a time). The controls cannot show such a
// value, so the form shows it as it is, marked invalid, instead of as "Off" or
// an empty time. A blank value and any other field are never invalid here.
export function invalidFormattedNumber(field: DeviceTypeFieldDefinition, raw: string): boolean {
  if (field.kind !== "number" || raw.trim() === "") return false;
  if (field.format === "weekday_mask") return parseWholeNumber(raw, WEEKDAY_MASK_MAX) === null;
  if (field.format === "minute_of_day") return parseWholeNumber(raw, MINUTE_OF_DAY_MAX) === null;
  return false;
}

// weekdayMaskDays says, per day (indexed as WEEKDAY_NAMES), whether a mask in
// form state sets it. A blank or invalid value sets none.
export function weekdayMaskDays(raw: string): boolean[] {
  const mask = parseWholeNumber(raw, WEEKDAY_MASK_MAX) ?? 0;
  return WEEKDAY_NAMES.map((_, bit) => (mask & (1 << bit)) !== 0);
}

// toggleWeekday returns the form value with one day turned on or off. It is
// always a number - "0" when no day is left - never "", because a blank number
// is left out of a save and "off" must be sendable.
export function toggleWeekday(raw: string, day: number, on: boolean): string {
  const mask = parseWholeNumber(raw, WEEKDAY_MASK_MAX) ?? 0;
  const bit = 1 << day;
  return String(on ? mask | bit : mask & ~bit);
}

// describeWeekdayMask reads a mask as "Sun, Wed", "Every day" or "Off". A
// value that is not a mask is returned as it is.
export function describeWeekdayMask(raw: string): string {
  const mask = parseWholeNumber(raw, WEEKDAY_MASK_MAX);
  if (mask === null) return raw;
  if (mask === 0) return "Off";
  if (mask === WEEKDAY_MASK_MAX) return "Every day";
  return WEEKDAY_NAMES.filter((_, bit) => (mask & (1 << bit)) !== 0).join(", ");
}

// minuteOfDayToTime turns minutes after midnight into "HH:MM" for a time
// input. A blank or out-of-range value gives "".
export function minuteOfDayToTime(raw: string): string {
  const minutes = parseWholeNumber(raw, MINUTE_OF_DAY_MAX);
  if (minutes === null) return "";
  const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

// timeToMinuteOfDay turns a time input's "HH:MM" (or "HH:MM:SS") into minutes
// after midnight as a form value; "" when the box is cleared or invalid.
export function timeToMinuteOfDay(time: string): string {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(time.trim());
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return "";
  return String(hours * 60 + minutes);
}

// describeConfigValue renders a form-state value the way an operator reads it:
// a choice's label rather than its wire value, On/Off for a checkbox, and an
// explicit "(empty)" so a blank is not mistaken for a missing value.
export function describeConfigValue(field: DeviceTypeFieldDefinition, raw: string): string {
  if (field.kind === "boolean") return raw === "true" ? "On" : "Off";
  if (field.kind === "secret") return raw === "" ? "Not set" : SECRET_MASK;
  if (raw === "") return "(empty)";
  if (field.kind === "number" && field.format === "weekday_mask") return describeWeekdayMask(raw);
  if (field.kind === "number" && field.format === "minute_of_day") return minuteOfDayToTime(raw) || raw;
  const choice = field.choices?.find((c) => c.value === raw);
  if (choice) return choice.label;
  const option = field.options?.find((o) => String(o.value) === raw);
  if (option) return option.label;
  if (field.kind === "string_array" || field.kind === "phone_array") {
    return raw.split("\n").filter(Boolean).join(", ") || "(empty)";
  }
  return raw;
}

// ---- object_array ----------------------------------------------------------
//
// An object_array (e.g. the gateway's EYE sensor entries) is edited as a list
// of items, each a flat Record<itemKey, string> of form state using the same
// string conventions as the top-level fields (string lists one per line).

export type ObjectArrayItemDraft = Record<string, string>;

export function emptyObjectArrayItem(field: DeviceTypeFieldDefinition): ObjectArrayItemDraft {
  const item: ObjectArrayItemDraft = {};
  for (const itemField of field.itemFields ?? []) {
    item[itemField.path] = itemField.kind === "boolean" ? "false" : "";
  }
  return item;
}

// objectArrayDraft turns a stored array into editable drafts. Anything that is
// not an array of objects yields no items.
export function objectArrayDraft(field: DeviceTypeFieldDefinition, value: unknown): ObjectArrayItemDraft[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === "object" && !Array.isArray(item))
    .map((item) => {
      const draft = emptyObjectArrayItem(field);
      for (const itemField of field.itemFields ?? []) {
        if (item[itemField.path] !== undefined) {
          draft[itemField.path] = stringifyFieldValue(itemField, item[itemField.path]);
        }
      }
      return draft;
    });
}

function isBlankItem(field: DeviceTypeFieldDefinition, draft: ObjectArrayItemDraft): boolean {
  return (field.itemFields ?? []).every((itemField) =>
    itemField.kind === "boolean" ? true : !hasConfigValue(itemField, draft[itemField.path] ?? ""),
  );
}

// objectArrayValue converts drafts back into the typed array. Every item key is
// always written - an empty label as "", an empty list as [] - because a device
// that reports its config compares whole arrays: an item missing a key it
// reports would never match, leaving a permanent shadow delta. Items with
// nothing in them are dropped (devices discard those too). Numbers that do not
// parse are left out.
export function objectArrayValue(
  field: DeviceTypeFieldDefinition,
  drafts: ObjectArrayItemDraft[],
): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];
  for (const draft of drafts) {
    if (isBlankItem(field, draft)) continue;
    const item: Record<string, unknown> = {};
    for (const itemField of field.itemFields ?? []) {
      const raw = draft[itemField.path] ?? "";
      if (itemField.kind === "number") {
        if (hasConfigValue(itemField, raw)) item[itemField.path] = Number(raw);
        continue;
      }
      const value = coerceConfigValue(itemField, raw);
      // MACs are stored upper-cased (the backend normalises them too), as the
      // device stores and reports them.
      item[itemField.path] = itemField.format === "mac" ? upperCaseMacs(value) : value;
    }
    out.push(item);
  }
  return out;
}

function upperCaseMacs(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((mac) => String(mac).toUpperCase());
  return typeof value === "string" ? value.toUpperCase() : value;
}

// serializedObjectArrayBytes is what maxSerializedBytes caps: the UTF-8 size of
// the value as compact JSON, built exactly as a save would send it (every item
// key present, MACs upper-cased) - the same measure the backend applies.
export function serializedObjectArrayBytes(field: DeviceTypeFieldDefinition, drafts: ObjectArrayItemDraft[]): number {
  return new TextEncoder().encode(JSON.stringify(objectArrayValue(field, drafts))).length;
}

// countObjectArrayValues counts what MaxTotalValues caps: the values in every
// string-list item field across all items.
export function countObjectArrayValues(field: DeviceTypeFieldDefinition, drafts: ObjectArrayItemDraft[]): number {
  let total = 0;
  for (const draft of drafts) {
    for (const itemField of field.itemFields ?? []) {
      if (itemField.kind !== "string_array") continue;
      total += (draft[itemField.path] ?? "").split("\n").map((v) => v.trim()).filter(Boolean).length;
    }
  }
  return total;
}

export function groupFieldsBySection(
  fields: DeviceTypeFieldDefinition[],
): Array<{ section: string; fields: DeviceTypeFieldDefinition[] }> {
  const order: string[] = [];
  const map = new Map<string, DeviceTypeFieldDefinition[]>();
  for (const field of fields) {
    const section = field.section?.trim() || "Configuration";
    if (!map.has(section)) {
      map.set(section, []);
      order.push(section);
    }
    map.get(section)!.push(field);
  }
  return order.map((section) => ({ section, fields: map.get(section)! }));
}
